import type { DashboardPoint, DashboardSlice } from '@/api/schema';
import { Link } from 'react-router-dom';

import styles from './Charts.module.css';

/**
 * Charts drawn with CSS and inline SVG rather than a charting library.
 *
 * Six shapes, none of them complicated, against a bundle that would grow by a hundred
 * kilobytes and a theming layer that would have to be taught the design tokens twice.
 * Every one of these reads its colours from the same variables everything else does.
 */

/** Shown wherever a chart has nothing to draw. */
export function NoData({ message }: { message: string }) {
  return <p className={styles.empty}>{message}</p>;
}

/** A horizontal bar per row, widths relative to the largest. */
export function Bars({
  rows,
  empty,
}: {
  rows: (DashboardSlice & { to?: string })[];
  empty: string;
}) {
  if (rows.length === 0) return <NoData message={empty} />;
  const largest = Math.max(...rows.map((row) => row.count), 1);

  return (
    <ul className={styles.bars}>
      {rows.map((row) => (
        <li key={row.label} className={styles.bar}>
          {row.to ? (
            <Link className={styles.barLink} to={row.to}>
              {row.label}
            </Link>
          ) : (
            <span className={styles.barLabel}>{row.label}</span>
          )}
          <span className={styles.barTrack}>
            <span
              className={styles.barFill}
              style={{ inlineSize: `${(row.count / largest) * 100}%` }}
            />
          </span>
          <span className={styles.barCount}>{row.count}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A weekly series as an area chart.
 *
 * Drawn as one SVG path over a 0–100 viewBox with `preserveAspectRatio="none"`, so it
 * stretches to whatever width the panel has without any measuring.
 */
export function Area({ points, empty }: { points: DashboardPoint[]; empty: string }) {
  if (points.length === 0) return <NoData message={empty} />;

  const peak = Math.max(...points.map((point) => point.count), 1);
  const step = points.length > 1 ? 100 / (points.length - 1) : 100;
  const coords = points.map((point, index) => ({
    x: index * step,
    y: 100 - (point.count / peak) * 92,
  }));

  const line =
    coords.length === 1
      ? `M0,${coords[0]?.y ?? 100} L100,${coords[0]?.y ?? 100}`
      : coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  const area = `${line} L100,100 L0,100 Z`;

  return (
    <div className={styles.area}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className={styles.areaSvg}
        role="img"
        aria-label={`${points.reduce((sum, p) => sum + p.count, 0)} candidates over ${points.length} weeks`}
      >
        <path d={area} className={styles.areaFill} />
        <path d={line} className={styles.areaLine} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className={styles.areaAxis}>
        <span>{points[0]?.week}</span>
        <span>Peak {peak}</span>
        <span>{points[points.length - 1]?.week}</span>
      </div>
      <details className={styles.weeklyDetails}>
        <summary>View weekly counts</summary>
        <ul>
          {points.map(({ week, count }) => (
            <li key={week}>
              <time dateTime={week}>{week}</time>
              <strong>{count}</strong>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
